import React from "react"

import { Autocomplete, TextField } from "@mui/material"
import { useFormikContext } from "formik"
import { getInNested } from "../dynamic-form.helper"
import { isWholeIncidentIntent } from "../../../../data-utils/roleMatchUtils"
import "./input-role.scss"

// Free-text Role picker (FIELD_TYPES.ROLE_INPUT). A themed MUI Autocomplete in freeSolo mode: the
// setter can type ANY role/title (resolved server-side to a scenario role), while the scenario's roles
// plus "All roles" are offered as type-ahead. The value is a STRING (verbatim typed text or a picked
// option); gameSessionUtils.soloRolesFromRoleFieldValue turns it into the soloRoles wire shape.
// Resolution feedback is shown ONCE, at the bottom of the players list (the "Scored on" summary in
// new-game-form-details.jsx) — NOT inline per-field (user direction 2026-09-18).

function InputRole(props) {
  const { id, label, name, placeholder, errorMessage, fieldData, isNestedFieldName, isRequired } = props
  const { values, errors, touched, setFieldValue } = useFormikContext()
  const normalizedNameKey = isNestedFieldName ? name : `['${name}']`
  const current = getInNested(values, name, isNestedFieldName)
  // Value is a STRING; tolerate a legacy array (comma-join) so an old saved value still shows.
  const value = typeof current === "string" ? current : Array.isArray(current) ? current.join(", ") : current ? String(current) : ""
  const invalid = getInNested(touched, name, isNestedFieldName) && getInNested(errors, name, isNestedFieldName)
  const disabled = fieldData?.enabled === false

  const options = Array.isArray(fieldData?.suggestions) ? [...new Set(fieldData.suggestions.map((s) => String(s ?? "").trim()).filter(Boolean))] : []

  // "All roles" (a whole-incident intent) is pinned to the top while the box is empty or the text
  // matches it; substring-filter the rest. Once someone types a role ("SOC Lead") it must not sit on
  // top of the list — with a highlighted first option, Enter used to replace the typed role with it.
  const filterOptions = (opts, state) => {
    const input = state.inputValue.trim().toLowerCase()
    const matches = (o) => !input || o.toLowerCase().includes(input)
    const pinned = opts.filter((o) => isWholeIncidentIntent(o) && matches(o))
    const rest = opts.filter((o) => !isWholeIncidentIntent(o) && matches(o))
    return [...pinned, ...rest]
  }

  const setVal = (v) => setFieldValue(normalizedNameKey, typeof v === "string" ? v : "")

  return (
    <div className="form-block-wrapper">
      <div className="custom-form-block flex-grow-1">
        <fieldset disabled={disabled}>
          <label htmlFor={id} className="form-label">
            {label} {isRequired && <sup>*</sup>}
          </label>
          <div className="ctd-role-autocomplete-wrap">
            <Autocomplete
              freeSolo
              // No autoHighlight: Enter keeps the TYPED role (freeSolo commits the input). A
              // suggestion is taken only when the person arrows to it or clicks it.
              handleHomeEndKeys
              selectOnFocus
              openOnFocus={false}
              blurOnSelect
              size="small"
              disabled={disabled}
              options={options}
              filterOptions={filterOptions}
              inputValue={value}
              onInputChange={(_e, newInput) => setVal(newInput)}
              onChange={(_e, newVal) => setVal(newVal)}
              className="ctd-role-autocomplete"
              slotProps={{ paper: { className: "ctd-role-paper" } }}
              renderOption={(liProps, option) => {
                const { key, ...rest } = liProps
                return (
                  <li
                    key={option}
                    {...rest}
                    className={`${rest.className || ""} ctd-role-opt${isWholeIncidentIntent(option) ? " ctd-role-opt-all" : ""}`}
                  >
                    {option}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  id={id}
                  placeholder={placeholder}
                  error={!!invalid}
                  className="ctd-role-autocomplete-input"
                  inputProps={{ ...params.inputProps, "aria-label": label }}
                />
              )}
            />
          </div>
          <div className="invalid-feedback d-block">{invalid ? errorMessage : ""}</div>
        </fieldset>
      </div>
    </div>
  )
}

InputRole.propTypes = {}

export default InputRole
